import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { businessNow } from 'helpers/businessDay'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsFilters from 'pages/main/analytics/parts/useAnalyticsFilters'
import { buildAnalyticsUrl, parseLocalDate } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { FilterField, FilterSelect, ExcelButton, useChainLoad } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, buildRanks, chainColor, planTone,
	RankBadge, FillCell, useSort, SortTh, ChainKpi, ChainEmpty, TableSearch,
} from '../parts/chainUi'

// Показатели плана филиала целиком: ключи ответа {id}_plan / {id}_fact / {id}_percent …
// Суммируемые — с прогнозом и «нужно в день», средние и проценты — только план, факт и %
const SUM_METRICS = ['revenue', 'cheques', 'guests', 'items', 'gross_profit', 'net_profit']
const RATIO_METRICS = ['avg_cheque', 'margin', 'food_cost']
const TOTAL_METRICS = [...SUM_METRICS, ...RATIO_METRICS]
const MONEY_METRICS = ['revenue', 'gross_profit', 'net_profit', 'avg_cheque']
const PERCENT_METRICS = ['margin', 'food_cost']
// Меньше — лучше: выполнение = план / факт, отклонение вверх — плохо
const LOWER_IS_BETTER = ['food_cost']
// Разрезы плана продаж (sales_plans.dimension)
const DIMENSIONS = ['order_type', 'channel', 'source', 'category']
const DIMENSION_ICONS = { total: 'uil-store', order_type: 'uil-list-ul', channel: 'uil-sitemap', source: 'uil-truck', category: 'uil-apps' }
// Экран «Планы» сразу на месяце отчёта и нужной вкладке
function plansUrl(tab, month) {
	const params = new URLSearchParams()
	if (month) params.set('month', month)
	if (tab === 'expenses') params.set('tab', 'expenses')
	const query = params.toString()
	return query ? `/chain/plans?${query}` : '/chain/plans'
}
const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

// Месяц как «2026-10» — по бизнес-дню: в 01:30 1-го числа это ещё прошлый месяц
function currentMonth() {
	const now = businessNow()
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function monthOf(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function dayLabel(value) {
	return value ? value.split('-').reverse().join('.') : ''
}

function isRatio(id) {
	return RATIO_METRICS.includes(id)
}

// Тип колонки Excel под показатель
function excelType(id) {
	if (PERCENT_METRICS.includes(id)) return 'percent'
	if (MONEY_METRICS.includes(id)) return 'money'
	return id === 'items' ? 'number' : 'int'
}

// Накопленные доли дней месяца в плане филиала (раскладка по весу дня недели).
// Нет раскладки — поровну.
function cumulativeShares(shares, daysInMonth) {
	const list = shares && shares.length ? shares : Array.from({ length: daysInMonth }, () => 1 / daysInMonth)
	let running = 0
	return list.map(value => {
		running += Number(value || 0)
		return running
	})
}

function PlanFact() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [tab, setTab] = useState('sales')
	const [dimension, setDimension] = useState('total')
	const [metric, setMetric] = useState('revenue')
	const [dimensionMetric, setDimensionMetric] = useState('revenue')
	const [chartMetric, setChartMetric] = useState('revenue')
	const [search, setSearch] = useState('')
	const [branchDetails, setBranchDetails] = useState(null)
	const [details, setDetails] = useState(null)

	// Экран месяца, а не периода: в фильтре нет period, поэтому общий период аналитики
	// (sessionStorage) не подхватывается и не перезаписывается — переносятся только филиалы
	const [runSearch, loading] = useChainLoad(searchWithFilter)
	const filters = { ...useAnalyticsFilters({ pos_ids: [], month: currentMonth(), kinds: ['expense'] }, runSearch), loading }
	const { draft, patch, applied } = filters

	const kindOptions = [
		{ id: 'expense', name: t('chain_kind_expense') },
		{ id: 'supplier', name: t('chain_kind_supplier') },
		{ id: 'staff', name: t('chain_kind_staff') },
	]

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/plan-fact', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const isCurrent = data?.state === 'current'
	const isPast = data?.state === 'past'
	const sales = data?.sales
	const expenses = data?.expenses
	const salesBranches = useMemo(() => data?.sales?.branches || [], [data])
	const salesTotals = sales?.totals || {}

	const metricLabels = {
		revenue: t('revenue'),
		cheques: t('cheques'),
		guests: t('guests'),
		items: t('chain_items'),
		gross_profit: t('gross_profit'),
		net_profit: t('net_profit'),
		avg_cheque: t('avg_cheque'),
		margin: t('margin'),
		food_cost: t('chain_food_cost'),
	}

	const dimensionLabels = {
		total: t('chain_pf_dim_total'),
		order_type: t('order_type'),
		channel: t('chain_pf_dim_channel'),
		source: t('chain_pf_dim_source'),
		category: t('categories'),
	}

	function formatValue(id, value) {
		if (value === null || value === undefined) return '—'
		if (PERCENT_METRICS.includes(id)) return percent(value)
		return MONEY_METRICS.includes(id) ? money(value) : formatMoney(Math.round(Number(value)))
	}

	// Название значения разреза: типы заказа, «свои заказы» и «без канала» — переводом
	function valueName(dimensionId, item) {
		if (dimensionId === 'order_type') {
			return { 1: t('hall'), 2: t('takeaway'), 3: t('delivery') }[item.id] || item.name
		}
		if (dimensionId === 'source' && Number(item.id) === 0) return t('chain_pf_own_orders')
		if (dimensionId === 'channel' && Number(item.id) === 0) return t('chain_pf_no_channel')
		return item.name
	}

	const monthLabel = data ? `${t(MONTH_KEYS[Number(data.month.slice(5, 7)) - 1])} ${data.month.slice(0, 4)}` : ''
	const factLabel = data?.end_date ? `${t('chain_fact_to')} ${dayLabel(data.end_date)}` : t('chain_month_not_started')

	// Показатели, у которых хоть у одного филиала есть план; выручка — всегда
	const plannedMetrics = useMemo(() => TOTAL_METRICS.filter(id => id === 'revenue'
		|| salesBranches.some(item => Number(item[`${id}_plan`] || 0) > 0)), [salesBranches])
	const activeMetric = metric === 'all' || plannedMetrics.includes(metric) ? metric : 'revenue'

	// Разрезы с планами; «Филиал целиком» — всегда
	const dimensionIds = ['total', ...DIMENSIONS.filter(id => sales?.dimensions?.[id]?.has_plans)]
	const activeDimension = dimensionIds.includes(dimension) ? dimension : 'total'

	// Сортировка: в режиме «все показатели» — по выполнению выручки, в детальном — по колонкам метрики
	const percentKey = activeMetric === 'all' ? 'revenue_percent' : `${activeMetric}_percent`
	const { sorted, sort, toggle } = useSort(salesBranches, percentKey)
	const { ranks, count } = useMemo(() => buildRanks(salesBranches, percentKey, true), [salesBranches, percentKey])

	const plannedRevenue = salesBranches.filter(item => item.revenue_plan > 0)
	const doneCount = plannedRevenue.filter(item => Number(item.revenue_percent) >= 100).length
	// Будущий месяц: выполнения ещё нет — лучшего и худшего тоже
	const executed = plannedRevenue.filter(item => item.revenue_percent !== null && item.revenue_percent !== undefined)
	const bestExecution = executed.length > 0 ? [...executed].sort((a, b) => b.revenue_percent - a.revenue_percent)[0] : null
	const worstExecution = executed.length > 1 ? [...executed].sort((a, b) => a.revenue_percent - b.revenue_percent)[0] : null

	const paceStats = useMemo(() => columnStats(salesBranches, `${activeMetric === 'all' ? 'revenue' : activeMetric}_pace`), [salesBranches, activeMetric])

	// Сменили показатель — сортируем по его выполнению
	function changeMetric(id) {
		setMetric(id)
		const key = id === 'all' ? 'revenue_percent' : `${id}_percent`
		if (sort.key !== key) toggle(key)
	}

	function changeDimension(id) {
		setDimension(id)
		const metrics = sales?.dimensions?.[id]?.metrics || []
		if (id !== 'total' && !metrics.includes(dimensionMetric)) setDimensionMetric(metrics[0] || 'revenue')
	}

	function openBranch(branch) {
		const series = (sales?.daily?.series || []).find(item => item.pos_id === branch.pos_id)
		setBranchDetails({ branch, series })
	}

	// ---------- Расходы ----------

	const expenseBranches = useMemo(() => data?.expenses?.branches || [], [data])
	const expenseTotals = expenses?.totals || {}
	const purposes = useMemo(() => {
		const list = data?.expenses?.purposes || []
		const query = search.trim().toLowerCase()
		return query ? list.filter(item => item.purpose_name.toLowerCase().includes(query)) : list
	}, [data, search])
	const readExpenseSort = (row, key) => key.startsWith('pos:') ? Number(row.poses?.[key.slice(4)]?.fact || 0) : row[key]
	const expenseSort = useSort(purposes, 'plan', 'desc', readExpenseSort)
	const expenseBranchStats = useMemo(() => columnStats(expenseBranches, 'percent'), [expenseBranches])
	const branchById = useMemo(() => Object.fromEntries(expenseBranches.map(item => [item.pos_id, item])), [expenseBranches])

	async function openExpenseDetails(posId, purpose) {
		const response = await GET(buildAnalyticsUrl('/chain/report/plan-fact-expenses-details', {
			pos_ids: applied.pos_ids,
			kinds: applied.kinds,
			start_date: data.start_date,
			end_date: data.end_date,
		}, {
			pos_id: posId || null,
			purpose_id: purpose ? purpose.purpose_id : null,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({
				...response.data,
				pos_name: posId ? branchById[posId]?.pos_name : t('chain_all_branches'),
				purpose_name: purpose ? purpose.purpose_name : t('chain_all_purposes'),
			})
		}
	}

	// ---------- Excel ----------

	function exportExcel() {
		if (!data) return
		const subtitle = `${t('month')}: ${monthLabel} · ${factLabel}`
		const metricColumns = []
		plannedMetrics.forEach(id => {
			const type = excelType(id)
			metricColumns.push({ label: `${metricLabels[id]} — ${t('plan')}`, type })
			metricColumns.push({ label: `${metricLabels[id]} — ${t('fact')}`, type })
			metricColumns.push({ label: `${metricLabels[id]} — ${t('chain_execution')}`, type: 'percent', width: 12 })
			if (isCurrent && !isRatio(id)) {
				metricColumns.push({ label: `${metricLabels[id]} — ${t('chain_forecast_short')}`, type })
				metricColumns.push({ label: `${metricLabels[id]} — ${t('chain_need_per_day')}`, type })
				metricColumns.push({ label: `${metricLabels[id]} — ${t('chain_pf_need_today')}`, type })
			}
		})
		const metricValues = (item) => plannedMetrics.flatMap(id => {
			const values = [item[`${id}_plan`] || null, item[`${id}_fact`], item[`${id}_percent`]]
			if (isCurrent && !isRatio(id)) values.push(item[`${id}_forecast`], item[`${id}_need_per_day`], item[`${id}_need_today`])
			return values
		})
		const byRevenue = buildRanks(salesBranches, 'revenue_percent', true).ranks
		const daily = sales?.daily || { days: [], series: [] }

		// Разрезы с планами: строка «значение × филиал», затем итог значения по сети
		const branchName = Object.fromEntries(salesBranches.map(item => [item.pos_id, item.pos_name]))
		const dimensionSheets = DIMENSIONS.filter(id => sales?.dimensions?.[id]?.has_plans).map(id => {
			const block = sales.dimensions[id]
			const metrics = block.metrics.filter(code => block.values.some(value => Number(value.total?.[code]?.plan || 0) > 0)
				|| code === 'revenue')
			const cellValues = (cell) => metrics.flatMap(code => [cell?.[code]?.plan ?? null, cell?.[code]?.fact ?? null, cell?.[code]?.percent ?? null])
			const rows = []
			block.values.forEach(value => {
				const name = `${'  '.repeat(value.depth || 0)}${valueName(id, value)}`
				Object.entries(value.poses || {}).forEach(([posId, cell]) => rows.push([name, branchName[posId] || posId, ...cellValues(cell)]))
				rows.push([name, t('chain_network'), ...cellValues(value.total)])
			})
			return {
				name: dimensionLabels[id],
				title: `${t('chain_plan_fact_title')} — ${dimensionLabels[id]}`,
				subtitle,
				columns: [
					{ label: dimensionLabels[id], type: 'text', width: 32 },
					{ label: t('chain_branch'), type: 'text', width: 28 },
					...metrics.flatMap(code => [
						{ label: `${metricLabels[code]} — ${t('plan')}`, type: excelType(code) },
						{ label: `${metricLabels[code]} — ${t('fact')}`, type: excelType(code) },
						{ label: `${metricLabels[code]} — ${t('chain_execution')}`, type: 'percent', width: 12 },
					]),
				],
				rows,
				footer: [t('total'), t('chain_network'), ...cellValues(block.total)],
			}
		})

		const purposeRows = data.expenses?.purposes || []
		downloadExcel(`${t('chain_plan_fact_title')} ${data.month}`, [
			{
				name: t('chain_section_sales'),
				title: `${t('chain_plan_fact_title')} — ${t('chain_section_sales')} — ${dimensionLabels.total}`,
				subtitle,
				columns: [
					{ label: t('chain_rank'), type: 'int', width: 8 },
					{ label: t('chain_branch'), type: 'text', width: 30 },
					...metricColumns,
				],
				rows: [...salesBranches]
					.sort((a, b) => Number(b.revenue_percent ?? -1) - Number(a.revenue_percent ?? -1))
					.map(item => [byRevenue[item.pos_id] || '', item.pos_name, ...metricValues(item)]),
				footer: ['', t('total'), ...metricValues(salesTotals)],
			},
			...dimensionSheets,
			{
				name: t('chain_by_days'),
				title: `${t('chain_by_days')} — ${t('revenue')}`,
				subtitle,
				columns: [
					{ label: t('date'), type: 'text', width: 12 },
					...daily.series.map(series => ({ label: series.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
				],
				rows: daily.days.map((day, index) => {
					const values = daily.series.map(series => series.revenue[index])
					return [dayLabel(day), ...values, values.reduce((sum, value) => sum + Number(value || 0), 0)]
				}),
			},
			{
				name: t('expenses'),
				title: `${t('chain_plan_fact_title')} — ${t('expenses')}`,
				subtitle,
				columns: [
					{ label: t('chain_purpose'), type: 'text', width: 34 },
					...expenseBranches.flatMap(branch => [
						{ label: `${branch.pos_name} — ${t('plan')}`, type: 'money' },
						{ label: `${branch.pos_name} — ${t('fact')}`, type: 'money' },
						{ label: `${branch.pos_name} — %`, type: 'percent', width: 10 },
					]),
					{ label: `${t('total')} — ${t('plan')}`, type: 'money' },
					{ label: `${t('total')} — ${t('fact')}`, type: 'money' },
					{ label: `${t('total')} — %`, type: 'percent', width: 10 },
				],
				rows: purposeRows.map(purpose => [
					purpose.purpose_name,
					...expenseBranches.flatMap(branch => {
						const cell = purpose.poses?.[branch.pos_id]
						return [cell?.plan || null, cell?.fact || null, cell?.percent ?? null]
					}),
					purpose.plan || null, purpose.fact, purpose.percent,
				]),
				footer: [
					t('total'),
					...expenseBranches.flatMap(branch => [branch.plan || null, branch.fact, branch.percent]),
					expenseTotals.plan || null, expenseTotals.fact, expenseTotals.percent,
				],
			},
			{
				name: `${t('expenses')} — ${t('chain_branches')}`,
				title: `${t('chain_plan_fact_title')} — ${t('expenses')} — ${t('chain_branches')}`,
				subtitle,
				columns: [
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('plan'), type: 'money' },
					{ label: t('chain_fact_planned'), type: 'money', width: 18 },
					{ label: t('chain_execution'), type: 'percent', width: 12 },
					{ label: t('chain_unplanned'), type: 'money' },
					{ label: t('chain_total_expenses'), type: 'money' },
					{ label: t('revenue'), type: 'money' },
					{ label: t('chain_percent_of_revenue'), type: 'percent', width: 14 },
				],
				rows: expenseBranches.map(branch => [
					branch.pos_name, branch.plan || null, branch.fact_planned, branch.percent, branch.unplanned,
					branch.fact, branch.revenue, branch.revenue_percent,
				]),
				footer: [t('total'), expenseTotals.plan || null, expenseTotals.fact_planned, expenseTotals.percent, expenseTotals.unplanned,
					expenseTotals.fact, expenseTotals.revenue, expenseTotals.revenue_percent],
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('expenses')} — ${details.purpose_name} — ${details.pos_name}`, [{
			name: t('chain_operations'),
			title: `${details.purpose_name} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(data?.start_date, data?.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('chain_purpose'), type: 'text', width: 28 },
				{ label: t('payment_type'), type: 'text', width: 18 },
				{ label: t('cashier'), type: 'text', width: 16 },
				{ label: t('note'), type: 'text', width: 36 },
				{ label: t('amount'), type: 'money' },
			],
			rows: details.rows.map(row => [
				formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), row.pos_name, row.purpose_name, row.payment_type_name,
				row.cashier_login || '', row.note || '', row.amount,
			]),
			footer: [t('total'), '', '', '', '', '', details.total],
		}])
	}

	// Планы продаж и расходов задаются на одном экране «Планы» — все филиалы сразу
	const setPlansButton = (
		<Link to={plansUrl(tab, data?.month || applied.month)} className="btn btn-sm btn-outline-primary" style={{ borderRadius: '10px', fontWeight: 600 }}>
			<i className="uil uil-crosshair me-1"></i>
			{tab === 'expenses' ? t('chain_set_expense_plan') : t('chain_set_sales_plan')}
		</Link>
	)

	return (
		<>
			<ChainFilterBar
				title={t('chain_plan_fact_title')}
				icon="uil-crosshair"
				subtitle={data ? <>{monthLabel} · {factLabel}</> : null}
				filters={filters}
				presets={false}
				wide
				actions={setPlansButton}
				onExcel={exportExcel}
				excelDisabled={!data}
				tabs={[
					{ id: 'sales', label: t('chain_section_sales'), icon: 'uil-chart-growth' },
					{ id: 'expenses', label: t('expenses'), icon: 'uil-money-withdraw' },
				]}
				activeTab={tab}
				onTabChange={setTab}
			>
				<FilterField label={t('month')}>
					<div className="calendar-input">
						<DatePicker
							showMonthYearPicker
							className="form-control"
							dateFormat="MM.yyyy"
							portalId="chain-month-picker"
							selected={parseLocalDate(`${draft.month}-01`)}
							onChange={(date) => date && patch({ month: monthOf(date) })}
						/>
						<i className="uil uil-calendar-alt"></i>
					</div>
				</FilterField>
				{tab === 'expenses' && (
					<FilterField label={t('chain_expense_kinds')}>
						<FilterSelect isMulti options={kindOptions} allLabel={t('chain_all_kinds')} value={draft.kinds} onChange={(ids) => patch({ kinds: ids })} />
					</FilterField>
				)}
			</ChainFilterBar>

			{data && tab === 'sales' && !sales.has_plans && (
				<PlansEmpty kind="sales" month={data?.month} />
			)}
			{data && tab === 'expenses' && !expenses.has_plans && (
				<PlansEmpty kind="expenses" month={data?.month} />
			)}

			{data && tab === 'sales' && sales.has_plans && (
				<>
					{sales.has_total_plans && (
						<div className="row g-3 mb-3">
							<div className="col-xl-2 col-md-4 col-6">
								<ChainKpi title={t('chain_execution_revenue')} icon="uil-crosshair" color={toneColor(planTone(salesTotals.revenue_percent))}
									value={percent(salesTotals.revenue_percent)}
									sub={salesTotals.revenue_plan > 0
										? <>{money(salesTotals.revenue_fact_planned)} {t('chain_from')} {money(salesTotals.revenue_plan)}</>
										: t('chain_no_plan')} />
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								{isCurrent ? (
									<ChainKpi title={t('chain_forecast')} icon="uil-chart-line" color={toneColor(planTone(salesTotals.revenue_forecast_percent))}
										title2={t('chain_pf_forecast_hint')}
										value={money(salesTotals.revenue_forecast)}
										sub={<><b>{percent(salesTotals.revenue_forecast_percent)}</b> {t('chain_of_plan')}</>} />
								) : (
									<ChainKpi title={t('chain_deviation')} icon="uil-arrows-v" color={Number(salesTotals.revenue_diff) < 0 ? '#f06548' : '#12b886'}
										value={salesTotals.revenue_diff === null || salesTotals.revenue_diff === undefined ? '—' : money(salesTotals.revenue_diff)}
										sub={t('revenue')} />
								)}
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								{isPast && (
									<ChainKpi title={t('chain_plan_done')} icon="uil-check-circle" color="#12b886"
										value={`${doneCount} / ${plannedRevenue.length}`}
										sub={t('chain_branches')} />
								)}
								{isCurrent && (
									<ChainKpi title={t('chain_pf_need_today')} icon="uil-calendar-alt" color="#f59f00"
										title2={t('chain_pf_need_today_hint')}
										value={formatValue('revenue', salesTotals.revenue_need_today)}
										sub={<>{t('chain_pf_on_average')}: <b>{formatValue('revenue', salesTotals.revenue_need_per_day)}</b> · {t('chain_days_left')}: <b>{data.days_left}</b></>} />
								)}
								{!isPast && !isCurrent && (
									<ChainKpi title={t('chain_need_per_day')} icon="uil-calendar-alt" color="#f59f00"
										title2={t('chain_need_per_day_hint')}
										value={formatValue('revenue', salesTotals.revenue_need_per_day)}
										sub={<>{t('chain_days_left')}: <b>{data.days_left}</b></>} />
								)}
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<ChainKpi title={t('gross_profit')} icon="uil-chart-pie-alt" color="#7c4dff" title2={t('chain_pf_gross_profit_hint')}
									value={salesTotals.gross_profit_plan > 0 ? percent(salesTotals.gross_profit_percent) : money(salesTotals.gross_profit_fact)}
									sub={salesTotals.gross_profit_plan > 0
										? <>{money(salesTotals.gross_profit_fact_planned)} {t('chain_from')} {money(salesTotals.gross_profit_plan)}</>
										: <>{t('margin')}: {percent(salesTotals.margin_fact)}</>} />
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<ChainKpi title={t('chain_best_execution')} icon="uil-trophy" color="#12b886"
									value={bestExecution ? bestExecution.pos_name : '—'}
									sub={bestExecution ? <>{percent(bestExecution.revenue_percent)} · {money(bestExecution.revenue_fact)}</> : null} />
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<ChainKpi title={t('chain_worst_execution')} icon="uil-arrow-down-right" color="#f06548"
									value={worstExecution ? worstExecution.pos_name : '—'}
									sub={worstExecution ? <>{percent(worstExecution.revenue_percent)} · {money(worstExecution.revenue_fact)}</> : null} />
							</div>
						</div>
					)}

					{dimensionIds.length > 1 && (
						<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
							<span className="chain-muted">{t('chain_pf_breakdown')}:</span>
							<FilterSegment
								options={dimensionIds.map(id => ({ id, label: dimensionLabels[id], icon: DIMENSION_ICONS[id] }))}
								value={activeDimension}
								onChange={changeDimension}
							/>
						</div>
					)}

					{activeDimension === 'total' && (
						<>
							{!sales.has_total_plans && (
								<div className="chain-note mb-3">
									<i className="uil uil-info-circle"></i>
									<span>{t('chain_pf_no_total_plans')}</span>
								</div>
							)}
							<div className="card chain-card mb-3">
								<div className="card-body">
									<div className="chain-toolbar">
										<div className="chain-toolbar-group">
											<h5 className="mb-0 me-2">{t('chain_plan_execution')}</h5>
											<FilterSegment
												options={[{ id: 'all', label: t('chain_all_metrics') }, ...plannedMetrics.map(id => ({ id, label: metricLabels[id] }))]}
												value={activeMetric}
												onChange={changeMetric}
											/>
										</div>
										<PlanLegend />
									</div>

									<div className="chain-scroll">
										{activeMetric === 'all' ? (
											<AllMetricsTable
												metrics={plannedMetrics} rows={sorted} totals={salesTotals} ranks={ranks} count={count} sort={sort} toggle={toggle}
												branches={salesBranches} metricLabels={metricLabels} formatValue={formatValue} onBranch={openBranch}
											/>
										) : (
											<MetricTable
												id={activeMetric} rows={sorted} totals={salesTotals} ranks={ranks} count={count} sort={sort} toggle={toggle}
												branches={salesBranches} isCurrent={isCurrent} isPast={isPast} formatValue={formatValue}
												paceStats={paceStats} onBranch={openBranch}
											/>
										)}
									</div>
									<div className="chain-muted mt-2">
										<i className="uil uil-info-circle me-1"></i>
										{t('chain_plan_fact_hint')} {t('chain_pf_weights_hint')}
										{salesTotals.planned_branches < salesBranches.length && <> {t('chain_planned_branches_note')}</>}
									</div>
								</div>
							</div>

							<div className="row g-3 mb-3">
								<div className="col-xl-8">
									<CumulativeChart data={data} metric={chartMetric} onMetric={setChartMetric} />
								</div>
								<div className="col-xl-4">
									<ExecutionChart branches={salesBranches} />
								</div>
							</div>
						</>
					)}

					{activeDimension !== 'total' && (
						<DimensionMatrix
							key={activeDimension}
							dimensionId={activeDimension}
							block={sales.dimensions[activeDimension]}
							title={dimensionLabels[activeDimension]}
							metric={(sales.dimensions[activeDimension].metrics || []).includes(dimensionMetric) ? dimensionMetric : 'revenue'}
							onMetric={setDimensionMetric}
							branches={salesBranches}
							metricLabels={metricLabels}
							formatValue={formatValue}
							valueName={(item) => valueName(activeDimension, item)}
							isCurrent={isCurrent}
						/>
					)}
				</>
			)}

			{data && tab === 'expenses' && expenses.has_plans && (
				<>
					{(applied.kinds || []).length === 1 && applied.kinds[0] === 'expense' && (
						<div className="chain-note mb-3">
							<i className="uil uil-info-circle"></i>
							<span>{t('chain_plan_kinds_note')}</span>
						</div>
					)}

					<div className="row g-3 mb-3">
						<div className="col-xl-3 col-md-6">
							<ChainKpi title={t('chain_expense_plan')} icon="uil-crosshair" color="#5b73e8"
								value={money(expenseTotals.plan)}
								sub={<>{t('chain_fact_planned')}: {money(expenseTotals.fact_planned)}</>} />
						</div>
						<div className="col-xl-3 col-md-6">
							<ChainKpi title={t('chain_execution')} icon="uil-percentage" color={Number(expenseTotals.percent) > 100 ? '#f06548' : '#12b886'}
								value={percent(expenseTotals.percent)}
								sub={expenseTotals.diff !== null && expenseTotals.diff !== undefined
									? <>{Number(expenseTotals.diff) > 0 ? t('chain_overspent') : t('chain_left_to_plan')}: <b>{money(Math.abs(expenseTotals.diff))}</b></>
									: null} />
						</div>
						<div className="col-xl-3 col-md-6">
							<ChainKpi title={t('chain_unplanned')} icon="uil-exclamation-triangle" color="#f59f00" title2={t('chain_unplanned_hint')}
								value={money(expenseTotals.unplanned)}
								sub={<>{t('chain_total_expenses')}: {money(expenseTotals.fact)}</>} />
						</div>
						<div className="col-xl-3 col-md-6">
							<ChainKpi title={t('chain_overspent_cells')} icon="uil-fire" color={expenseTotals.over_cells > 0 ? '#f06548' : '#12b886'}
								value={formatMoney(expenseTotals.over_cells || 0)}
								sub={<>{percent(expenseTotals.revenue_percent)} {t('chain_of_revenue')}</>} />
						</div>
					</div>

					<div className="card chain-card mb-3">
						<div className="card-body">
							<div className="chain-toolbar">
								<div className="chain-toolbar-group">
									<h5 className="mb-0 me-2">{t('chain_by_purposes')}</h5>
								</div>
								<div className="chain-toolbar-group">
									<span className="chain-legend">
										<span className="chain-dot" style={{ background: 'rgba(18, 184, 134, 0.8)' }}></span>{t('chain_within_plan')}
										<span className="chain-dot ms-2" style={{ background: 'rgba(240, 101, 72, 0.8)' }}></span>{t('chain_overspent')}
									</span>
									<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_purpose')} />
								</div>
							</div>

							<div className="chain-scroll">
								<table className="chain-table">
									<thead>
										<tr>
											<th className="chain-index chain-sticky">#</th>
											<SortTh sortKey="purpose_name" sort={expenseSort.sort} onSort={expenseSort.toggle} className="chain-sticky-2">{t('chain_purpose')}</SortTh>
											{expenseBranches.map((branch, index) => (
												<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={expenseSort.sort} onSort={expenseSort.toggle} className="text-end">
													<span className="chain-dot" style={{ background: chainColor(index) }}></span>
													{branch.pos_name}
												</SortTh>
											))}
											<SortTh sortKey="plan" sort={expenseSort.sort} onSort={expenseSort.toggle} className="text-end chain-total-col">{t('plan')}</SortTh>
											<SortTh sortKey="fact" sort={expenseSort.sort} onSort={expenseSort.toggle} className="text-end chain-total-col">{t('fact')}</SortTh>
											<SortTh sortKey="percent" sort={expenseSort.sort} onSort={expenseSort.toggle} className="text-end">{t('chain_execution')}</SortTh>
										</tr>
									</thead>
									<tbody>
										{expenseSort.sorted.length === 0 && <ChainEmpty colSpan={expenseBranches.length + 5} />}
										{expenseSort.sorted.map((purpose, rowIndex) => (
											<tr key={purpose.purpose_id}>
												<td className="chain-index chain-sticky">{rowIndex + 1}</td>
												<td className="chain-name chain-sticky-2 chain-click" onClick={() => purpose.fact > 0 && openExpenseDetails(null, purpose)} title={t('chain_open_details')}>
													<span>{purpose.purpose_name}</span>
													{purpose.plan <= 0 && <div className="chain-muted">{t('chain_no_plan')}</div>}
												</td>
												{expenseBranches.map(branch => (
													<ExpenseCell key={branch.pos_id} cell={purpose.poses?.[branch.pos_id]}
														onClick={() => openExpenseDetails(branch.pos_id, purpose)} />
												))}
												<td className="chain-num chain-total-col">{purpose.plan > 0 ? money(purpose.plan) : '—'}</td>
												<td className="chain-num chain-total-col">{money(purpose.fact)}</td>
												{purpose.percent === null
													? <td className="chain-num chain-muted">—</td>
													: <FillCell percent={Math.min(100, purpose.percent)} tone={purpose.percent > 100 ? 'danger' : 'success'}>{percent(purpose.percent)}</FillCell>}
											</tr>
										))}
									</tbody>
									{expenseSort.sorted.length > 0 && (
										<tfoot>
											<tr>
												<td className="chain-sticky"></td>
												<td className="chain-sticky-2">{t('plan')}</td>
												{expenseBranches.map(branch => (
													<td key={branch.pos_id} className="chain-num">{branch.plan > 0 ? money(branch.plan) : '—'}</td>
												))}
												<td className="chain-num chain-total-col">{money(expenseTotals.plan)}</td>
												<td className="chain-num chain-total-col"></td>
												<td></td>
											</tr>
											<tr>
												<td className="chain-sticky"></td>
												<td className="chain-sticky-2" title={t('chain_fact_planned_hint')}>{t('chain_fact_planned')}</td>
												{expenseBranches.map(branch => (
													<td key={branch.pos_id} className="chain-num chain-click" style={heatStyle(branch.percent, expenseBranchStats, false)}
														onClick={() => branch.fact > 0 && openExpenseDetails(branch.pos_id, null)}>
														{money(branch.fact_planned)}
														<div className="chain-muted">{percent(branch.percent)}</div>
													</td>
												))}
												<td className="chain-num chain-total-col"></td>
												<td className="chain-num chain-total-col">{money(expenseTotals.fact_planned)}</td>
												<td className="chain-num">{percent(expenseTotals.percent)}</td>
											</tr>
											<tr>
												<td className="chain-sticky"></td>
												<td className="chain-sticky-2" title={t('chain_unplanned_hint')}>{t('chain_unplanned')}</td>
												{expenseBranches.map(branch => (
													<td key={branch.pos_id} className="chain-num">{branch.unplanned > 0 ? money(branch.unplanned) : '—'}</td>
												))}
												<td className="chain-num chain-total-col"></td>
												<td className="chain-num chain-total-col">{money(expenseTotals.unplanned)}</td>
												<td></td>
											</tr>
										</tfoot>
									)}
								</table>
							</div>
							<div className="chain-muted mt-2">
								<i className="uil uil-info-circle me-1"></i>
								{t('chain_plan_expenses_hint')}
							</div>
						</div>
					</div>

					<div className="row g-3 mb-3">
						<div className="col-12">
							<ExpensesChart branches={expenseBranches} />
						</div>
					</div>
				</>
			)}

			{/* MODAL BRANCH */}
			<Modal show={!!branchDetails} onHide={() => setBranchDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{branchDetails?.branch.pos_name}
						<div className="chain-muted fw-normal">{monthLabel} · {factLabel}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{branchDetails && <BranchDays data={data} branch={branchDetails.branch} series={branchDetails.series} />}
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-secondary" onClick={() => setBranchDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			{/* MODAL EXPENSE DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.purpose_name} — {details?.pos_name}
						<div className="chain-muted fw-normal">{periodText(data?.start_date, data?.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_operations_lower')} · {money(details?.total)}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('date')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('chain_purpose')}</th>
								<th>{t('payment_type')}</th>
								<th>{t('cashier')}</th>
								<th>{t('note')}</th>
								<th className="text-end">{t('amount')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={8} />}
							{(details?.rows || []).map((row, index) => (
								<tr key={row.id}>
									<td className="chain-index">{index + 1}</td>
									<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
									<td>{row.pos_name}</td>
									<td>
										{row.purpose_name}
										{row.kind !== 'expense' && <div className="chain-muted">{t(`chain_kind_${row.kind}`)}</div>}
									</td>
									<td>{row.payment_type_name}</td>
									<td>{row.cashier_login || '—'}</td>
									<td style={{ maxWidth: '280px' }}>{row.note || '—'}</td>
									<td className="chain-num"><b>{money(row.amount)}</b></td>
								</tr>
							))}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

function toneColor(tone) {
	if (tone === 'success') return '#12b886'
	if (tone === 'warning') return '#f59f00'
	return '#f06548'
}

// Шкала цвета выполнения плана
function PlanLegend() {
	return (
		<span className="chain-legend">
			<span className="chain-dot" style={{ background: 'rgba(240, 101, 72, 0.7)' }}></span>&lt; 80%
			<span className="chain-dot ms-2" style={{ background: 'rgba(245, 159, 0, 0.8)' }}></span>80–100%
			<span className="chain-dot ms-2" style={{ background: 'rgba(18, 184, 134, 0.8)' }}></span>≥ 100%
		</span>
	)
}

// Отклонение факта от плана: зелёным — в лучшую сторону (у фудкоста — вниз)
function DiffValue({ id, value, formatValue }) {
	if (value === null || value === undefined) return <span>—</span>
	const number = Number(value)
	const good = LOWER_IS_BETTER.includes(id) ? number <= 0 : number >= 0
	const text = PERCENT_METRICS.includes(id) ? `${number > 0 ? '+' : ''}${String(Number(number).toFixed(1)).replace('.', ',')}` : `${number > 0 ? '+' : ''}${formatValue(id, value)}`
	return <span style={{ color: good ? '#12b886' : '#f06548', fontWeight: 600 }}>{text}</span>
}

// Планы не заданы — объясняем, где их задать, вместо пустых нулей.
// Планы продаж и расходов задаются на экране «Планы» сразу для всех филиалов.
function PlansEmpty({ kind, month }) {
	const { t } = useTranslation()
	const isSales = kind === 'sales'

	return (
		<div className="card chain-card mb-3">
			<div className="card-body chain-plans-empty">
				<div className="chain-plans-empty-icon"><i className="uil uil-crosshair"></i></div>
				<h5>{isSales ? t('chain_plans_empty_sales') : t('chain_plans_empty_expenses')}</h5>
				<p>{isSales ? t('chain_pf_plans_empty_sales_hint') : t('chain_plans_empty_expenses_hint')}</p>
				<Link to={plansUrl(kind, month)} className="btn btn-primary chain-plans-empty-main">
					<i className="uil uil-plus me-1"></i>
					{isSales ? t('chain_set_sales_plan') : t('chain_set_expense_plan')}
				</Link>
			</div>
		</div>
	)
}

// Ячейка выполнения: процент с полосой и цветом, под ним факт / план
function ExecutionCell({ id, item, formatValue, onClick, style }) {
	const { t } = useTranslation()
	const plan = Number(item[`${id}_plan`] || 0)
	const fact = item[`${id}_fact`]
	if (plan <= 0) {
		return (
			<td className="chain-num" style={style}>
				<span className="chain-muted">{t('chain_no_plan')}</span>
				<div>{formatValue(id, fact)}</div>
			</td>
		)
	}
	const value = item[`${id}_percent`]
	return (
		<FillCell percent={Math.min(100, Number(value || 0))} tone={planTone(value)} onClick={onClick} style={style}
			title={`${t('fact')}: ${formatValue(id, fact)} · ${t('plan')}: ${formatValue(id, plan)}`}>
			<b>{percent(value)}</b>
			<div className="chain-muted">{formatValue(id, fact)} / {formatValue(id, plan)}</div>
		</FillCell>
	)
}

// Граница между суммами и средними/процентами — отдельной группой справа
const RATIO_GROUP_BORDER = { borderLeft: '2px solid var(--m-border, #edf0f4)' }

// Все показатели сразу: где филиал отстаёт — по выручке, чекам, гостям или фудкосту
function AllMetricsTable({ metrics, rows, totals, ranks, count, sort, toggle, branches, metricLabels, formatValue, onBranch }) {
	const { t } = useTranslation()
	const firstRatio = metrics.find(id => isRatio(id))
	const groupStyle = id => id === firstRatio ? RATIO_GROUP_BORDER : undefined
	return (
		<table className="chain-table">
			<thead>
				<tr>
					<th className="chain-index chain-sticky">#</th>
					<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
					{metrics.map(id => (
						<SortTh key={id} sortKey={`${id}_percent`} sort={sort} onSort={toggle} className="text-end"
							style={{ minWidth: '140px', ...(groupStyle(id) || {}) }}
							title={id === 'food_cost' ? t('chain_pf_food_cost_hint') : undefined}>
							{metricLabels[id]}
						</SortTh>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 && <ChainEmpty colSpan={metrics.length + 2} />}
				{rows.map(item => (
					<tr key={item.pos_id}>
						<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
						<BranchNameCell item={item} branches={branches} onBranch={onBranch} />
						{metrics.map(id => (
							<ExecutionCell key={id} id={id} item={item} formatValue={formatValue} style={groupStyle(id)} />
						))}
					</tr>
				))}
			</tbody>
			{rows.length > 0 && (
				<tfoot>
					<tr>
						<td className="chain-sticky"></td>
						<td className="chain-sticky-2">{t('total')}</td>
						{metrics.map(id => (
							<td key={id} className="chain-num" style={groupStyle(id)}>
								{totals[`${id}_plan`] > 0 ? percent(totals[`${id}_percent`]) : '—'}
								<div className="chain-muted">
									{formatValue(id, totals[`${id}_plan`] > 0 ? totals[`${id}_fact_planned`] : totals[`${id}_fact`])}
									{totals[`${id}_plan`] > 0 && <> / {formatValue(id, totals[`${id}_plan`])}</>}
								</div>
							</td>
						))}
					</tr>
				</tfoot>
			)}
		</table>
	)
}

function BranchNameCell({ item, branches, onBranch }) {
	const { t } = useTranslation()
	const index = branches.indexOf(item)
	return (
		<td className="chain-name chain-sticky-2 chain-click" onClick={() => onBranch(item)} title={t('chain_open_details')}>
			<span className="d-inline-flex align-items-center">
				<span className="chain-dot" style={{ background: chainColor(index) }}></span>
				{item.pos_name}
			</span>
			{!item.has_plan && <div className="chain-muted">{t('chain_no_plan')}</div>}
		</td>
	)
}

// Один показатель подробно: план, факт, отклонение, по графику, темп, прогноз и сколько нужно
// в день и сегодня. Средний чек, маржа и фудкост — только план, факт, выполнение и отклонение
function MetricTable({ id, rows, totals, ranks, count, sort, toggle, branches, isCurrent, isPast, formatValue, paceStats, onBranch }) {
	const { t } = useTranslation()
	const ratio = isRatio(id)
	const showPace = isCurrent && !ratio
	const showNeed = !isPast && !ratio
	const showToday = isCurrent && !ratio
	const columns = 6 + (showPace ? 4 : 0) + (showNeed ? 1 : 0) + (showToday ? 1 : 0)

	return (
		<table className="chain-table">
			<thead>
				<tr>
					<th className="chain-index chain-sticky">#</th>
					<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
					<SortTh sortKey={`${id}_plan`} sort={sort} onSort={toggle} className="text-end">{t('plan')}</SortTh>
					<SortTh sortKey={`${id}_fact`} sort={sort} onSort={toggle} className="text-end">{t('fact')}</SortTh>
					<SortTh sortKey={`${id}_percent`} sort={sort} onSort={toggle} className="text-end" style={{ minWidth: '140px' }}
						title={id === 'food_cost' ? t('chain_pf_food_cost_hint') : undefined}>{t('chain_execution')}</SortTh>
					<SortTh sortKey={`${id}_diff`} sort={sort} onSort={toggle} className="text-end">{t('chain_deviation')}</SortTh>
					{showPace && <SortTh sortKey={`${id}_expected`} sort={sort} onSort={toggle} className="text-end" title={t('chain_pf_by_schedule_hint')}>{t('chain_by_schedule')}</SortTh>}
					{showPace && <SortTh sortKey={`${id}_pace`} sort={sort} onSort={toggle} className="text-end" title={t('chain_pace_hint')}>{t('chain_pace')}</SortTh>}
					{showPace && <SortTh sortKey={`${id}_forecast`} sort={sort} onSort={toggle} className="text-end" title={t('chain_pf_forecast_hint')}>{t('chain_forecast_short')}</SortTh>}
					{showPace && <SortTh sortKey={`${id}_forecast_percent`} sort={sort} onSort={toggle} className="text-end" style={{ minWidth: '120px' }} title={t('chain_pf_forecast_hint')}>{t('chain_forecast_percent')}</SortTh>}
					{showNeed && <SortTh sortKey={`${id}_need_per_day`} sort={sort} onSort={toggle} className="text-end" title={t('chain_need_per_day_hint')}>{t('chain_need_per_day')}</SortTh>}
					{showToday && <SortTh sortKey={`${id}_need_today`} sort={sort} onSort={toggle} className="text-end" title={t('chain_pf_need_today_hint')}>{t('chain_pf_need_today')}</SortTh>}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 && <ChainEmpty colSpan={columns} />}
				{rows.map(item => {
					const hasPlan = Number(item[`${id}_plan`] || 0) > 0
					return (
						<tr key={item.pos_id}>
							<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
							<BranchNameCell item={item} branches={branches} onBranch={onBranch} />
							<td className="chain-num">{hasPlan ? formatValue(id, item[`${id}_plan`]) : <span className="chain-muted">{t('chain_no_plan')}</span>}</td>
							<td className="chain-num"><b>{formatValue(id, item[`${id}_fact`])}</b></td>
							<ExecutionCell id={id} item={item} formatValue={formatValue} />
							<td className="chain-num"><DiffValue id={id} value={item[`${id}_diff`]} formatValue={formatValue} /></td>
							{showPace && <td className="chain-num">{formatValue(id, item[`${id}_expected`])}</td>}
							{showPace && <td className="chain-num" style={heatStyle(item[`${id}_pace`], paceStats)}>{percent(item[`${id}_pace`])}</td>}
							{showPace && <td className="chain-num">{formatValue(id, item[`${id}_forecast`])}</td>}
							{showPace && (hasPlan
								? <FillCell percent={Math.min(100, Number(item[`${id}_forecast_percent`] || 0))} tone={planTone(item[`${id}_forecast_percent`])}>{percent(item[`${id}_forecast_percent`])}</FillCell>
								: <td className="chain-num chain-muted">—</td>)}
							{showNeed && <td className="chain-num">{formatValue(id, item[`${id}_need_per_day`])}</td>}
							{showToday && <td className="chain-num"><b>{formatValue(id, item[`${id}_need_today`])}</b></td>}
						</tr>
					)
				})}
			</tbody>
			{rows.length > 0 && (
				<tfoot>
					<tr>
						<td className="chain-sticky"></td>
						<td className="chain-sticky-2">{t('total')}</td>
						<td className="chain-num">{formatValue(id, totals[`${id}_plan`])}</td>
						<td className="chain-num">{formatValue(id, totals[`${id}_fact`])}</td>
						<td className="chain-num">{percent(totals[`${id}_percent`])}</td>
						<td className="chain-num"><DiffValue id={id} value={totals[`${id}_diff`]} formatValue={formatValue} /></td>
						{showPace && <td className="chain-num">{formatValue(id, totals[`${id}_expected`])}</td>}
						{showPace && <td className="chain-num">{percent(totals[`${id}_pace`])}</td>}
						{showPace && <td className="chain-num">{formatValue(id, totals[`${id}_forecast`])}</td>}
						{showPace && <td className="chain-num">{percent(totals[`${id}_forecast_percent`])}</td>}
						{showNeed && <td className="chain-num">{formatValue(id, totals[`${id}_need_per_day`])}</td>}
						{showToday && <td className="chain-num">{formatValue(id, totals[`${id}_need_today`])}</td>}
					</tr>
				</tfoot>
			)}
		</table>
	)
}

// Ячейка разреза «значение × филиал»: % выполнения с заливкой, под ним факт / план.
// Без плана — только факт, приглушённо
function DimensionCell({ cell, metric, formatValue, isCurrent, total = false }) {
	const { t } = useTranslation()
	const plan = Number(cell?.plan || 0)
	const fact = total && plan > 0 ? cell?.fact_planned : cell?.fact
	const totalClass = total ? ' chain-total-col' : ''
	if (!cell || (plan <= 0 && !Number(cell.fact || 0))) {
		return <td className={`chain-num chain-muted${totalClass}`}>—</td>
	}
	if (plan <= 0) {
		return (
			<td className={`chain-num${totalClass}`} title={t('chain_no_plan')}>
				<span className="chain-muted">{formatValue(metric, cell.fact)}</span>
			</td>
		)
	}
	const hints = [`${t('fact')}: ${formatValue(metric, fact)}`, `${t('plan')}: ${formatValue(metric, plan)}`]
	if (isCurrent && cell.forecast !== undefined) hints.push(`${t('chain_forecast_short')}: ${formatValue(metric, cell.forecast)}`)
	if (cell.need_per_day !== undefined) hints.push(`${t('chain_need_per_day')}: ${formatValue(metric, cell.need_per_day)}`)
	if (cell.need_today !== undefined) hints.push(`${t('chain_pf_need_today')}: ${formatValue(metric, cell.need_today)}`)
	return (
		<FillCell percent={Math.min(100, Number(cell.percent || 0))} tone={planTone(cell.percent)} title={hints.join(' · ')}
			className={totalClass.trim()}>
			<b>{percent(cell.percent)}</b>
			<div className="chain-muted">{formatValue(metric, fact)} / {formatValue(metric, plan)}</div>
		</FillCell>
	)
}

// Покрытие плана: сумма планов разреза к плану филиала целиком
function CoverageMark({ value }) {
	const { t } = useTranslation()
	if (value === null || value === undefined) return null
	const ok = Math.abs(Number(value) - 100) < 0.5
	return (
		<div className={`chain-plan-coverage ${ok ? 'ok' : 'warn'}`} title={t('chain_pf_coverage_hint')}>
			{t('chain_pf_coverage')}: {percent(value)}
		</div>
	)
}

// Разрез плана: значения (тип заказа, канал, агрегатор, категория) × филиалы
function DimensionMatrix({ dimensionId, block, title, metric, onMetric, branches, metricLabels, formatValue, valueName, isCurrent }) {
	const { t } = useTranslation()
	const [query, setQuery] = useState('')
	const isCategory = dimensionId === 'category'
	const columns = block.columns || {}
	const columnBranches = branches.filter(branch => columns[branch.pos_id])
	const coverage = block.coverage?.[metric] || null

	const rows = useMemo(() => {
		const text = query.trim().toLowerCase()
		const list = block.values.map(item => ({ ...item, display_name: valueName(item) }))
		return text ? list.filter(item => item.display_name.toLowerCase().includes(text)) : list
	}, [block, query]) // eslint-disable-line react-hooks/exhaustive-deps

	// Сортировка: по умолчанию — порядок разреза (у категорий — дерево)
	const readSort = (row, key) => {
		if (key === 'display_name') return row.display_name
		if (key === 'total') return row.total?.[metric]?.percent ?? null
		if (key === 'total_fact') return row.total?.[metric]?.fact ?? null
		if (key.startsWith('pos:')) {
			const cell = row.poses?.[key.slice(4)]?.[metric]
			return cell ? (cell.percent ?? null) : null
		}
		return row[key]
	}
	const { sorted, sort, toggle } = useSort(rows, null, 'desc', readSort)
	const isTree = isCategory && !sort.key && !query.trim()

	// Филиалы, где сумма планов разреза не равна плану филиала целиком
	const offCoverage = coverage
		? Object.entries(coverage.poses || {}).filter(([, value]) => Math.abs(Number(value) - 100) >= 0.5)
		: []
	const branchName = Object.fromEntries(branches.map(branch => [String(branch.pos_id), branch.pos_name]))

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-toolbar">
					<div className="chain-toolbar-group">
						<h5 className="mb-0 me-2">{title}</h5>
						<FilterSegment
							options={block.metrics.map(id => ({ id, label: metricLabels[id] }))}
							value={metric}
							onChange={onMetric}
						/>
					</div>
					<div className="chain-toolbar-group">
						<PlanLegend />
						{block.values.length > 12 && <TableSearch value={query} onChange={setQuery} placeholder={t('search')} />}
					</div>
				</div>

				{offCoverage.length > 0 && (
					<div className="chain-note mb-2">
						<i className="uil uil-exclamation-triangle" style={{ color: '#f59f00' }}></i>
						<span>
							{isCategory ? t('chain_pf_coverage_category_warning') : t('chain_pf_coverage_warning')}{' '}
							{offCoverage.map(([posId, value]) => `${branchName[posId] || posId} — ${percent(value)}`).join(', ')}
						</span>
					</div>
				)}

				<div className="chain-scroll">
					<table className="chain-table">
						<thead>
							<tr>
								<th className="chain-index chain-sticky">#</th>
								<SortTh sortKey="display_name" sort={sort} onSort={toggle} className="chain-sticky-2">{title}</SortTh>
								{columnBranches.map(branch => (
									<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end" style={{ minWidth: '130px' }}>
										<span className="chain-dot" style={{ background: chainColor(branches.indexOf(branch)) }}></span>
										{branch.pos_name}
									</SortTh>
								))}
								<SortTh sortKey="total" sort={sort} onSort={toggle} className="text-end chain-total-col" style={{ minWidth: '140px' }}>{t('chain_network')}</SortTh>
							</tr>
						</thead>
						<tbody>
							{sorted.length === 0 && <ChainEmpty colSpan={columnBranches.length + 3} />}
							{sorted.map((item, index) => (
								<tr key={item.id}>
									<td className="chain-index chain-sticky">{index + 1}</td>
									<td className="chain-name chain-sticky-2">
										<span style={isTree && item.depth ? { paddingLeft: `${item.depth * 16}px` } : undefined}>
											{isTree && item.depth > 0 && <i className="uil uil-corner-down-right-alt chain-muted me-1"></i>}
											{item.display_name}
										</span>
										{!item.has_plan && <div className="chain-muted">{t('chain_no_plan')}</div>}
									</td>
									{columnBranches.map(branch => (
										<DimensionCell key={branch.pos_id} cell={item.poses?.[branch.pos_id]?.[metric]} metric={metric}
											formatValue={formatValue} isCurrent={isCurrent} />
									))}
									<DimensionCell cell={item.total?.[metric]} metric={metric} formatValue={formatValue} isCurrent={isCurrent} total />
								</tr>
							))}
						</tbody>
						{sorted.length > 0 && (
							<tfoot>
								<tr>
									<td className="chain-sticky"></td>
									<td className="chain-sticky-2" title={isCategory ? t('chain_pf_category_total_hint') : undefined}>{t('total')}</td>
									{columnBranches.map(branch => {
										const cell = columns[branch.pos_id]?.[metric]
										const plan = Number(cell?.plan || 0)
										return (
											<td key={branch.pos_id} className="chain-num">
												{plan > 0 ? percent(cell.percent) : formatValue(metric, cell?.fact)}
												{plan > 0 && <div className="chain-muted">{formatValue(metric, cell.fact_planned)} / {formatValue(metric, plan)}</div>}
												<CoverageMark value={coverage?.poses?.[branch.pos_id]} />
											</td>
										)
									})}
									<td className="chain-num chain-total-col">
										{Number(block.total?.[metric]?.plan || 0) > 0 ? percent(block.total[metric].percent) : formatValue(metric, block.total?.[metric]?.fact)}
										{Number(block.total?.[metric]?.plan || 0) > 0 && (
											<div className="chain-muted">{formatValue(metric, block.total[metric].fact_planned)} / {formatValue(metric, block.total[metric].plan)}</div>
										)}
										<CoverageMark value={coverage?.total} />
									</td>
								</tr>
							</tfoot>
						)}
					</table>
				</div>
				<div className="chain-muted mt-2">
					<i className="uil uil-info-circle me-1"></i>
					{t('chain_pf_dimension_hint')}
					{isCategory && <> {t('chain_pf_category_total_hint')}</>}
				</div>
			</div>
		</div>
	)
}

// Ячейка «статья × филиал»: факт, план и цвет — уложились или перерасход
function ExpenseCell({ cell, onClick }) {
	const { t } = useTranslation()
	if (!cell || (Number(cell.plan) <= 0 && Number(cell.fact) <= 0)) {
		return <td className="chain-num chain-muted">—</td>
	}
	const plan = Number(cell.plan)
	const fact = Number(cell.fact)
	if (plan <= 0) {
		return (
			<td className="chain-num chain-click" onClick={onClick} style={{ background: 'rgba(245, 159, 0, 0.12)' }} title={t('chain_unplanned_hint')}>
				<span>{money(fact)}</span>
				<div className="chain-muted">{t('chain_no_plan')}</div>
			</td>
		)
	}
	const over = fact > plan
	return (
		<FillCell percent={Math.min(100, Number(cell.percent || 0))} tone={over ? 'danger' : 'success'}
			onClick={fact > 0 ? onClick : undefined}
			title={`${t('fact')}: ${money(fact)} · ${t('plan')}: ${money(plan)}${cell.quantity ? ` · ${formatMoney(cell.quantity)} ${t('chain_operations_lower')}` : ''}`}>
			<span style={{ fontWeight: over ? 800 : 600, color: over ? '#f06548' : undefined }}>{money(fact)}</span>
			<div className="chain-muted">{t('chain_from')} {money(plan)} · {percent(cell.percent, 0)}</div>
		</FillCell>
	)
}

// Накопление с начала месяца против плана, разложенного по дням недели, и прогноз до конца
// месяца в том же темпе. Сравниваются только филиалы, у которых план по показателю задан
function CumulativeChart({ data, metric, onMetric }) {
	const { t } = useTranslation()
	const daily = data.sales.daily
	const planKey = `${metric}_plan`
	const daysInMonth = data.days_in_month
	const month = data.month
	const planned = data.sales.branches.filter(item => Number(item[planKey] || 0) > 0)
	const included = (planned.length > 0 ? planned : data.sales.branches).map(item => item.pos_id)
	const shares = Object.fromEntries(included.map(posId => [posId, cumulativeShares(daily.plan_shares?.[posId], daysInMonth)]))

	const seriesByPos = Object.fromEntries(daily.series.map(series => [series.pos_id, series]))
	const factDays = daily.days.length
	const factByDay = daily.days.map((day, index) => included
		.reduce((sum, posId) => sum + Number(seriesByPos[posId]?.[metric]?.[index] || 0), 0))

	const categories = Array.from({ length: daysInMonth }, (item, index) => `${String(index + 1).padStart(2, '0')}.${month.slice(5, 7)}`)
	const cumulative = []
	let running = 0
	categories.forEach((label, index) => {
		if (index < factByDay.length) {
			running += factByDay[index]
			cumulative.push(Math.round(running))
		} else {
			cumulative.push(null)
		}
	})

	const series = [{ name: t('fact'), type: 'area', data: cumulative }]
	if (planned.length > 0) {
		series.push({
			name: t('chain_pf_plan_line'),
			type: 'line',
			data: categories.map((label, index) => Math.round(planned.reduce((sum, item) => sum + Number(item[planKey]) * shares[item.pos_id][index], 0))),
		})
	}
	// Прогноз филиала: факт к доле прошедших дней × накопленная доля дня — тот же темп с поправкой на дни недели
	if (data.state === 'current' && data.days_passed > 0) {
		const passed = data.days_passed
		const pace = Object.fromEntries(included.map(posId => {
			const fact = (seriesByPos[posId]?.[metric] || []).slice(0, passed).reduce((sum, value) => sum + Number(value || 0), 0)
			const share = shares[posId][passed - 1]
			return [posId, share > 0 ? fact / share : 0]
		}))
		series.push({
			name: t('chain_forecast_short'),
			type: 'line',
			data: categories.map((label, index) => index + 1 < factDays ? null
				: Math.round(included.reduce((sum, posId) => sum + pace[posId] * shares[posId][index], 0))),
		})
	}

	const format = value => value === null || value === undefined ? '—' : formatMoney(Math.round(value))
	const options = {
		chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
		colors: ['#12b886', '#5b73e8', '#f59f00'],
		stroke: { width: [3, 2, 2], curve: 'straight', dashArray: [0, 5, 3] },
		fill: { type: ['gradient', 'solid', 'solid'], gradient: { opacityFrom: 0.3, opacityTo: 0.02 } },
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories, tickAmount: Math.min(daysInMonth, 15), labels: { rotate: 0, hideOverlappingLabels: true } },
		yaxis: { labels: { formatter: value => metric === 'revenue' ? formatMoney(Math.round(value / 1000)) + 'k' : formatMoney(Math.round(value)) } },
		tooltip: { shared: true, y: { formatter: format } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_cumulative')}</h5>
					<FilterSegment
						options={[{ id: 'revenue', label: t('revenue') }, { id: 'cheques', label: t('cheques') }]}
						value={metric}
						onChange={onMetric}
					/>
				</div>
				<ReactApexChart key={`${metric}-${month}`} type="line" height={330} series={series} options={options} />
			</div>
		</div>
	)
}

// Выполнение плана выручки по филиалам: полоса до 100% и дальше
function ExecutionChart({ branches }) {
	const { t } = useTranslation()
	const rows = branches.filter(item => item.revenue_plan > 0).sort((a, b) => b.revenue_percent - a.revenue_percent)
	const series = [{ name: t('chain_execution'), data: rows.map(item => Number(item.revenue_percent || 0)) }]
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%', distributed: true } },
		colors: rows.map(item => toneColor(planTone(item.revenue_percent))),
		dataLabels: { enabled: true, formatter: value => percent(value, 0), style: { fontSize: '11px' } },
		legend: { show: false },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		annotations: { xaxis: [{ x: 100, borderColor: '#5b73e8', strokeDashArray: 4, label: { text: '100%', style: { color: '#fff', background: '#5b73e8' } } }] },
		xaxis: { categories: rows.map(item => item.pos_name), labels: { formatter: value => `${Math.round(value)}%` } },
		tooltip: { y: { formatter: value => percent(value) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_execution_revenue')}</h5></div>
				{rows.length > 0 && <ReactApexChart type="bar" height={Math.max(330, rows.length * 38 + 80)} series={series} options={options} />}
			</div>
		</div>
	)
}

// План и факт расходов по филиалам
function ExpensesChart({ branches }) {
	const { t } = useTranslation()
	const rows = branches.filter(item => item.plan > 0 || item.fact > 0)
	const series = [
		{ name: t('plan'), data: rows.map(item => Math.round(item.plan)) },
		{ name: t('chain_fact_planned'), data: rows.map(item => Math.round(item.fact_planned)) },
		{ name: t('chain_unplanned'), data: rows.map(item => Math.round(item.unplanned)) },
	]
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: { bar: { columnWidth: '60%', borderRadius: 2 } },
		colors: ['#5b73e8', '#f06548', '#f59f00'],
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(item => item.pos_name) },
		yaxis: { labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_plan_vs_fact_by_branches')}</h5>
				</div>
				{rows.length > 0 && <ReactApexChart type="bar" height={340} series={series} options={options} />}
			</div>
		</div>
	)
}

// Филиал по дням: выручка и чеки за день против плана дня (по весу дня недели), накопление
function BranchDays({ data, branch, series }) {
	const { t } = useTranslation()
	const days = data.sales.daily.days
	const planRevenue = Number(branch.revenue_plan || 0)
	const planCheques = Number(branch.cheques_plan || 0)
	const shares = data.sales.daily.plan_shares?.[branch.pos_id] || []
	const cumulativeShare = cumulativeShares(shares, data.days_in_month)
	let cumulative = 0
	const rows = days.map((day, index) => {
		const revenue = Number(series?.revenue?.[index] || 0)
		const cheques = Number(series?.cheques?.[index] || 0)
		cumulative += revenue
		const dayIndex = Number(day.slice(8, 10)) - 1
		const dayPlan = planRevenue > 0 ? planRevenue * Number(shares[dayIndex] ?? 1 / data.days_in_month) : null
		const planToDate = planRevenue > 0 ? planRevenue * cumulativeShare[dayIndex] : null
		return { day, revenue, cheques, cumulative, dayPlan, planToDate, pace: planToDate ? cumulative / planToDate * 100 : null }
	})
	const maxRevenue = Math.max(0, ...rows.map(row => row.revenue))
	const dailyPlan = planRevenue > 0 ? planRevenue / data.days_in_month : null

	return (
		<>
			<div className="d-flex flex-wrap gap-3 mb-3 chain-muted">
				<span>{t('plan')}: <b>{planRevenue > 0 ? money(planRevenue) : t('chain_no_plan')}</b></span>
				{dailyPlan && <span>{t('chain_plan_per_day')}: <b>{money(dailyPlan)}</b> ({t('chain_pf_on_average').toLowerCase()})</span>}
				{planCheques > 0 && <span>{t('cheques')}: <b>{formatMoney(planCheques)}</b></span>}
				<span>{t('chain_execution')}: <b>{percent(branch.revenue_percent)}</b></span>
				{branch.revenue_need_today !== null && branch.revenue_need_today !== undefined && (
					<span>{t('chain_pf_need_today')}: <b>{money(branch.revenue_need_today)}</b></span>
				)}
			</div>
			<table className="chain-table">
				<thead>
					<tr>
						<th>{t('date')}</th>
						<th className="text-end">{t('revenue')}</th>
						<th className="text-end" title={t('chain_pf_day_plan_hint')}>{t('chain_pf_day_plan')}</th>
						<th className="text-end">{t('cheques')}</th>
						<th className="text-end">{t('chain_cumulative_short')}</th>
						<th className="text-end" title={t('chain_pf_by_schedule_hint')}>{t('chain_by_schedule')}</th>
						<th className="text-end" style={{ minWidth: '120px' }}>{t('chain_pace')}</th>
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 && <ChainEmpty colSpan={7} />}
					{rows.map(row => (
						<tr key={row.day}>
							<td className="text-nowrap">{dayLabel(row.day)}</td>
							<FillCell percent={maxRevenue > 0 ? row.revenue / maxRevenue * 100 : 0}
								tone={row.dayPlan ? (row.revenue >= row.dayPlan ? 'success' : 'danger') : 'primary'}>
								{money(row.revenue)}
							</FillCell>
							<td className="chain-num chain-muted">{row.dayPlan !== null ? money(row.dayPlan) : '—'}</td>
							<td className="chain-num">{formatMoney(row.cheques)}</td>
							<td className="chain-num"><b>{money(row.cumulative)}</b></td>
							<td className="chain-num">{row.planToDate !== null ? money(row.planToDate) : '—'}</td>
							{row.pace !== null
								? <FillCell percent={Math.min(100, row.pace)} tone={planTone(row.pace)}>{percent(row.pace)}</FillCell>
								: <td className="chain-num chain-muted">—</td>}
						</tr>
					))}
				</tbody>
			</table>
		</>
	)
}

export default PlanFact
